import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Sports } from "@/api/sports";
import { AuthExpiredError } from "@/api/client";
import { TeamBadge, Skeleton, PlayerName } from "@/components/ui-kit";
import { DataState, SourceNote } from "@/components/DataState";
import { ncaaConferenceLabel, formatRecord } from "@/lib/sports";
import { isLiveStatus } from "@/lib/gameStatus";

function confLabel(team) {
  if (team.league === "NFL") return [team.conference, team.division].filter(Boolean).join(" ");
  return team.conference != null ? ncaaConferenceLabel(team.conference) : "—";
}

function Section({ title, children }) {
  return (
    <section className="mt-6">
      <h2 className="font-display tracking-wide text-lg text-white mb-3">{title}</h2>
      {children}
    </section>
  );
}

export default function TeamDetailPage() {
  const { id } = useParams();
  const [teamRes, setTeamRes] = useState(null);
  const [sched, setSched] = useState(null);
  const [stats, setStats] = useState(null);
  const [roster, setRoster] = useState(null);

  const load = () => {
    Sports.team(id).then((tr) => {
      setTeamRes(tr);
      // Use the /sports/team/{id} schedule if the contract includes it; only
      // fall back to deriving from /games when it intentionally omits schedule.
      if (tr.state === "ok" && Array.isArray(tr.schedule) && tr.schedule.length) {
        setSched({ state: "ok", games: tr.schedule, record: tr.record || null, fromContract: true });
      } else if (tr.state === "ok") {
        Sports.teamGames(id).then(setSched).catch(() => setSched({ state: "error", games: [], record: null }));
      }
    }).catch((e) => { if (!(e instanceof AuthExpiredError)) setTeamRes({ state: "error", team: null }); });
    Sports.teamStats(id).then(setStats).catch(() => setStats({ state: "error" }));
    Sports.roster(id).then(setRoster).catch(() => setRoster({ state: "error", players: [] }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!teamRes) return <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8"><Skeleton className="h-40 mb-4" /><Skeleton className="h-64" /></div>;
  if (teamRes.state === "not_found") return <div className="max-w-5xl mx-auto px-8 py-20 text-center text-white/50">Team not found.</div>;
  if (teamRes.state !== "ok") return <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8"><DataState state={teamRes.state} onRetry={load} title="Team unavailable" /></div>;

  const t = teamRes.team;
  const derivedRecord = sched?.record && (sched.record.w || sched.record.l || sched.record.t) ? formatRecord(sched.record) : null;
  const record = formatRecord(t.record) || formatRecord(teamRes.record) || derivedRecord;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6">
      <Link to="/teams" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-4" data-testid="back-to-teams"><ArrowLeft size={16} /> Teams</Link>

      <div className="tt-panel-3 rounded-2xl p-6 flex items-center gap-5" data-testid="team-header">
        <TeamBadge team={t} size={64} />
        <div className="min-w-0">
          <div className="font-display text-3xl text-white leading-tight">{t.name}</div>
          <div className="text-white/50 text-sm mt-1">{t.league} · {confLabel(t)}{record ? ` · ${record}` : ""}</div>
        </div>
      </div>
      <SourceNote timestamp={t.source_timestamp} coverage="Live provider feed" />

      <Section title="Schedule & results">
        {!sched ? <Skeleton className="h-32" />
          : sched.state === "ok" ? (
            <div className="space-y-2" data-testid="team-schedule">
              {sched.games.map((g) => (
                <Link key={g.id} to={`/game/${g.id}`} data-testid={`sched-row-${g.id}`}
                  className="flex items-center justify-between tt-panel rounded-lg px-3 py-2.5 hover:border-[#3A4859] transition-colors">
                  <span className="text-sm text-white truncate">{g.isHome ? "vs" : "@"} {g.opponent}</span>
                  <span className="text-xs tabnums shrink-0 flex items-center gap-2">
                    {g.result && <span className={g.result === "W" ? "text-[#4ea152]" : g.result === "L" ? "text-[#ff6b60]" : "text-white/50"}>{g.result}</span>}
                    {g.teamScore != null && g.oppScore != null ? <span className="text-white/70">{g.teamScore}-{g.oppScore}</span> : null}
                    <span className="text-white/40">{isLiveStatus(g.status) ? "LIVE" : (g.status || "").toUpperCase()}</span>
                  </span>
                </Link>
              ))}
            </div>
          ) : <DataState state={sched.state} title={sched.state === "empty" ? "No games yet" : undefined} detail={sched.state === "empty" ? "No games for this team in the current feed." : undefined} />}
      </Section>

      <Section title="Team season stats">
        {!stats ? <Skeleton className="h-24" />
          : stats.state === "ok" ? (
            <div className="space-y-3" data-testid="team-stats">
              <div className="tt-panel rounded-xl p-4 text-white/70 text-sm">Season stats loaded from the live provider feed.</div>
              {Array.isArray(stats.players) && stats.players.length > 0 && (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2" data-testid="team-stat-leaders">
                  {stats.players.slice(0, 12).map((pl, i) => (
                    <div key={pl.id || pl.player_id || i} className="tt-panel rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid={`stat-leader-${pl.id || pl.player_id || i}`}>
                      <PlayerName player={pl} className="text-sm text-white truncate" testid={`stat-leader-link-${pl.id || pl.player_id || i}`} />
                      <span className="text-[11px] text-white/45 shrink-0 truncate">{[pl.position, pl.stat_label || pl.category].filter(Boolean).join(" · ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : <DataState state={stats.state} title="Season stats not available yet" detail="Team season stats will appear here when the provider feed exposes them." />}
      </Section>

      <Section title="Player leaders & roster">
        {!roster ? <Skeleton className="h-24" />
          : roster.state === "ok" ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2" data-testid="team-roster">
              {roster.players.map((p, i) => {
                const injury = p.injury_status || p.injury || null;
                return (
                  <div key={p.id || i} className="tt-panel rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid={`roster-player-${p.id || i}`}>
                    <PlayerName player={p} className="text-sm text-white truncate" testid={`roster-player-link-${p.id || i}`} />
                    <span className="flex items-center gap-2 shrink-0">
                      {injury && <span className="text-[10px] font-display px-1.5 py-0.5 rounded bg-[#3a1e1e] text-[#ff8a80]" data-testid={`roster-injury-${p.id || i}`}>{injury}</span>}
                      <span className="text-[11px] text-white/45">{[p.position, (p.jersey ?? p.jersey_number) != null ? `#${p.jersey ?? p.jersey_number}` : null].filter(Boolean).join(" · ")}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : <DataState state={roster.state} title="Roster not available yet" detail="Player leaders and rosters will appear when the provider feed exposes them." />}
      </Section>
    </div>
  );
}
